import React, { useEffect, useRef, useState } from 'react';
import { getChildCategories, getRootCategories, searchCategories } from './productFinderApi';
import { countLabel } from './constants';
import styles from './ProductFinder.module.css';

const LEVEL_NAMES = ['Department', 'Subcategory', 'Sub-subcategory'];

// Browse department › subcategory to any depth, or search by name; the picks are
// sent as categories_include (products in any of them).
const CategoryPicker = ({ token, domain, selected, onChange }) => {
  const [levels, setLevels] = useState([]); // [{ nodes, picked }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const childCache = useRef({});
  const searchCache = useRef({});

  useEffect(() => {
    let cancelled = false;
    setLevels([]);
    setHits(null);
    setTerm('');
    setError('');
    childCache.current = {};
    setLoading(true);
    getRootCategories(token, domain)
      .then((nodes) => { if (!cancelled) setLevels([{ nodes, picked: null }]); })
      .catch((e) => { if (!cancelled) setError(`Could not load the category tree: ${e.message}`); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, domain]);

  const pick = async (depth, id) => {
    const base = levels.slice(0, depth + 1).map((l, i) => (i === depth ? { ...l, picked: null } : l));
    if (!id) { setLevels(base); return; }
    const node = base[depth].nodes.find((n) => String(n.id) === String(id));
    base[depth] = { ...base[depth], picked: node };
    setLevels(base);
    if (!node.children || !node.children.length || depth >= 11) return;
    try {
      setLoading(true);
      const key = `${domain}:${node.id}`;
      if (!childCache.current[key]) childCache.current[key] = await getChildCategories(token, domain, node.id);
      const kids = childCache.current[key];
      if (kids.length) setLevels([...base, { nodes: kids, picked: null }]);
    } catch (e) {
      setError(`Could not load subcategories: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const chain = levels.map((l) => l.picked).filter(Boolean);

  const add = (entry) => {
    if (selected.some((c) => c.id === entry.id)) return;
    onChange([...selected, entry]);
  };

  const addBrowsed = () => {
    const last = chain[chain.length - 1];
    add({ id: last.id, root: chain[0].id, name: last.name, path: chain.map((n) => n.name).join(' › ') });
  };

  const runSearch = async (e) => {
    e.preventDefault();
    const q = term.trim();
    if (!q.split(/\s+/).some((w) => w.length >= 3)) {
      setHits([]);
      return;
    }
    const key = `${domain}:${q.toLowerCase()}`;
    try {
      setSearching(true);
      if (!searchCache.current[key]) searchCache.current[key] = await searchCategories(token, domain, q);
      setHits(searchCache.current[key]);
    } catch (err) {
      setHits([]);
      setError(`Category search failed: ${err.message}`);
    } finally {
      setSearching(false);
    }
  };

  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>Category &amp; Subcategory</h3>
      {error && <p className={styles.warn}>{error}</p>}
      <div className={styles.catGrid}>
        <div>
          <p className={styles.caption}>
            Pick a department, then a subcategory, and keep going as deep as you like. Add any level, as many as you want.
          </p>
          {levels.map((level, depth) => (
            <label key={depth} className={styles.field}>
              <span className={styles.label}>{LEVEL_NAMES[depth] || `Level ${depth + 1}`}</span>
              <select
                className={styles.input}
                value={level.picked ? level.picked.id : ''}
                onChange={(e) => pick(depth, e.target.value)}
              >
                <option value="">— any —</option>
                {level.nodes.map((n) => (
                  <option key={n.id} value={n.id}>{`${n.name}  ·  ${countLabel(n.product_count)}`}</option>
                ))}
              </select>
            </label>
          ))}
          {loading && <p className={styles.caption}>Loading…</p>}
          {chain.length > 0 && (
            <button type="button" className={styles.secondaryBtn} onClick={addBrowsed}>
              + Add {chain[chain.length - 1].name}
            </button>
          )}
        </div>
        <div>
          <p className={styles.caption}>Or search by name, 3+ letters per word (1 token per new search).</p>
          <form onSubmit={runSearch} className={styles.inlineForm}>
            <input
              className={styles.input}
              placeholder="e.g. water bottles"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            <button type="submit" className={styles.secondaryBtn} disabled={searching}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </form>
          {hits && hits.length === 0 && <p className={styles.caption}>No matching categories.</p>}
          {hits && hits.length > 0 && (
            <ul className={styles.hitList}>
              {hits.map((h) => (
                <li key={h.id}>
                  <span>{h.path} <em>· {countLabel(h.product_count)}</em></span>
                  <button type="button" className={styles.linkBtn} onClick={() => add(h)}>Add</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className={styles.chips}>
        {selected.length === 0 && <span className={styles.caption}>No category selected: searching all categories.</span>}
        {selected.map((c) => (
          <span key={c.id} className={styles.chip}>
            {c.path}
            <button type="button" onClick={() => onChange(selected.filter((x) => x.id !== c.id))} aria-label="Remove">×</button>
          </span>
        ))}
      </div>
    </section>
  );
};

export default CategoryPicker;
